'use client';

import { ArrowLeft } from 'lucide-react';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { Suspense, useState } from 'react';
import { AppShell, mutationHeaders, responseMessage } from '../../../../components/app-shell';
import { documentLabels, documentTypes, isDocumentType, type DocumentType } from '../../../../modules/document/document-types';
import { TradeForm, type TradeSaveResult } from '../../../../modules/trade/components/trade-form';
import type { TradeInput } from '../../../../modules/trade/schema';

export default function NewTradePage() {
  return <Suspense fallback={<p>正在加载制单表单…</p>}><NewTradeForm /></Suspense>;
}

function NewTradeForm() {
  const router = useRouter();
  const params = useSearchParams();
  const requested = params.get('type');
  const [documentType, setDocumentType] = useState<DocumentType>(isDocumentType(requested) ? requested : 'QUOTATION');
  const [savedTrade, setSavedTrade] = useState<TradeSaveResult | null>(null);
  const [savedInput, setSavedInput] = useState<TradeInput | undefined>();
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function createDocument(result: TradeSaveResult) {
    setSavedTrade(result); setCreating(true); setError(null);
    try {
      const response = await fetch(`/api/trades/${result.tradeId}/documents`, { method: 'POST', headers: mutationHeaders(), body: JSON.stringify({ type: documentType }) });
      if (!response.ok) throw new Error(await responseMessage(response));
      const document = await response.json() as { documentId: string };
      router.push(`/documents/${document.documentId}`);
    } catch (caught) { setError(caught instanceof Error ? caught.message : '创建单据失败，交易草稿已保存。'); }
    finally { setCreating(false); }
  }
  return <AppShell active="trades">
    <div className="page-heading"><div><Link className="back-link" href="/trades"><ArrowLeft size={16} />返回交易</Link><p className="eyebrow">新的工作</p><h1>新建交易</h1><p className="page-subtitle">选择单据类型，填写后保存并预览。</p></div></div>
    <section className="editor-section"><label>单据类型<select value={documentType} disabled={Boolean(savedTrade) || creating} onChange={event => setDocumentType(event.target.value as DocumentType)}>{documentTypes.map(type => <option key={type} value={type}>{documentLabels[type]}</option>)}</select></label></section>
    {error ? <div role="alert" className="form-error">{error}{savedTrade ? <button type="button" className="secondary-button" disabled={creating} onClick={() => void createDocument(savedTrade)}>重试创建单据</button> : null}</div> : null}
    <TradeForm documentType={documentType} initial={savedInput} tradeId={savedTrade?.tradeId} revision={savedTrade?.revision} readOnly={creating || Boolean(savedTrade)} onSaved={(result, input) => { setSavedInput(input); void createDocument(result); }} />
  </AppShell>;
}
